import { Handle, Position } from '@xyflow/react'
import { Workflow, X, Zap, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface RecipeCallNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function RecipeCallNode({ data, selected, id }: RecipeCallNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const recipeId = typeof data.config?.recipe_id === 'string' ? data.config.recipe_id : ''
  const recipeName = typeof data.config?.recipe_name === 'string' ? data.config.recipe_name : recipeId
  const version = typeof data.config?.recipe_version === 'string' ? data.config.recipe_version : ''
  const mode = typeof data.config?.mode === 'string' ? data.config.mode : 'sync'
  const isAsync = mode === 'async'

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-48 transition-all ${
        selected
          ? 'border-error ring-2 ring-error/30'
          : 'border-error/30 hover:border-error'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Workflow size={16} className="text-error flex-shrink-0" />
          <div className="font-bold text-sm text-foreground truncate">
            {data.label || 'Sub-recette'}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-error-soft rounded text-error"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-muted-foreground mb-2 bg-card rounded p-2">
        {recipeName ? (
          <div className="space-y-1">
            <div className="text-foreground font-medium truncate">{recipeName}</div>
            {version && (
              <div className="text-muted-foreground">v{version}</div>
            )}
          </div>
        ) : (
          <div className="text-muted-foreground">{t('build.noRecipeConfigured', 'Aucune recette configurée')}</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className={`px-2 py-1 rounded flex items-center gap-1 ${
          isAsync
            ? 'bg-primary-soft text-accent-foreground'
            : 'bg-error-soft text-error'
        }`}>
          {isAsync ? <Clock size={12} /> : <Zap size={12} />}
          {isAsync ? 'async' : 'sync'}
        </span>
        {data.config?.output_key && (
          <span className="px-2 py-1 bg-muted text-foreground rounded truncate">
            → {String(data.config.output_key)}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
